import Contact from "../../../classes/Contact";
import ContactBalance from "../../../classes/ContactBalance";
import Address from "../../../classes/Address";
import { db } from "../../../boot/firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  setDoc,
  where,
} from "firebase/firestore";
import { call } from "../../../util/misc";

export const GET_BALANCE = async ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: ContactBalance,
    path: ContactBalance.getPath(id),
    stateKey: "balances",
    id,
    forceUpdate: true,
  });
};

export const LISTEN_BALANCE = async (
  { dispatch },
  { id, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: ContactBalance,
    path: ContactBalance.getPath(id),
    stateKey: "balances",
    id,
    next,
    error,
    unlisten,
  });
};

export const GET_CONTACT = async ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: Contact,
    path: Contact.getPath(id),
    stateKey: "contacts",
    id,
  });
};

export const GET_CONTACTS = (
  { dispatch },
  { conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: Contact,
    stateKey: "contacts",
    path: Contact.getPath(),
    conditions,
    limitCount,
    pagination,
  });
};

export const SEARCH_CONTACTS = async (
  { commit, state },
  { search, limit: limitCount = 20, type = null }
) => {
  if (!search) {
    // If no search string, rely on Firestore
    let q = query(
      collection(db, Contact.getPath()),
      orderBy("created", "desc"),
      limit(limitCount)
    );
    if (type) {
      if (Array.isArray(type)) {
        q = query(q, where("type", "in", type));
      } else {
        q = query(q, where("type", "==", type));
      }
    }
    const snap = await getDocs(q);
    return snap.docs.map((doc) => {
      const contact = new Contact(doc.id, doc.data());
      commit("SET", { stateKey: "contacts", stateID: doc.id, doc: contact });
      return contact;
    });
  }

  // Use client-side search across loaded contacts
  let matches = Object.values(state.contacts);

  if (type) {
    if (Array.isArray(type)) {
      matches = matches.filter((c) => type.includes(c.data.type));
    } else {
      matches = matches.filter((c) => c.data.type === type);
    }
  }

  const searchLower = search.toLowerCase();
  const scored = [];
  for (const c of matches) {
    const name = (c.data.name || "").toLowerCase();
    const company = (c.data.company || "").toLowerCase();
    let score = -1;
    if (name === searchLower) score = 0;
    else if (name.startsWith(searchLower)) score = 1;
    else if (company === searchLower) score = 2;
    else if (company.startsWith(searchLower)) score = 3;
    else if (name.includes(searchLower)) score = 4;
    else if (company.includes(searchLower)) score = 5;
    else if (
      c.data.position &&
      c.data.position.some((p) => p.toLowerCase().includes(searchLower))
    )
      score = 6;
    else if (
      c.data.emails &&
      c.data.emails.some(
        (e) => e.address && e.address.toLowerCase().includes(searchLower)
      )
    )
      score = 7;
    else if (
      c.data.phones &&
      c.data.phones.some((p) => p.number && p.number.includes(searchLower))
    )
      score = 8;

    if (score >= 0) scored.push({ c, score });
  }
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, limitCount).map(({ c }) => c);
};

export const LISTEN_CONTACT = ({ dispatch }, { id, next, error, unlisten }) => {
  return dispatch("LISTEN_DOC", {
    converter: Contact,
    path: Contact.getPath(id),
    stateKey: "contacts",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_CONTACTS = (
  { dispatch },
  { where, listenerPath, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: Contact,
    path: Contact.getPath(),
    stateKey: "contacts",
    where,
    listenerPath,
    next,
    error,
    unlisten,
  });
};

export const SAVE_CONTACT = async ({ commit, state }, contact) => {
  if (!contact.id) {
    // New Contact
    contact.data.created = new Date();
    contact.data.createdBy = state.user.id;
    const doc = await addDoc(
      collection(db, Contact.getPath()).withConverter(Contact),
      contact
    );
    contact.id = doc.id;
  } else {
    // Existing Contact
    contact.data.modified = new Date();
    contact.data.modifiedBy = state.user.id;
    await setDoc(doc(db, contact.path).withConverter(Contact), contact);
  }
  const savedContact = new Contact(contact.id, contact.data);
  commit("SET", {
    stateKey: "contacts",
    stateID: savedContact.id,
    doc: savedContact,
  });
  commit("CONTACT_SAVED", savedContact);

  // If this save resolves a pending "add new address" request for this
  // contact, detect the newly added address (the one whose locationKey was
  // not present before the edit dialog opened) so the originating field can
  // auto-populate it on return.
  const pendingAddress = state.pendingAddressSelection;
  if (
    pendingAddress &&
    pendingAddress.contactID === savedContact.id &&
    !pendingAddress.address
  ) {
    const existingKeys = new Set(pendingAddress.existingKeys || []);
    const newAddress = (savedContact.data.addresses || []).find(
      (address) => !existingKeys.has(new Address(address).locationKey())
    );
    if (newAddress) {
      commit("SET_PENDING_ADDRESS", {
        ...newAddress,
        contact: savedContact.id,
      });
    }
  }

  return savedContact;
};

export const MERGE_CONTACTS = async (context, groups) => {
  return call("mergeContacts", groups);
};

export const GET_IGNORED_DUPLICATE_GROUPS = async ({ commit }) => {
  try {
    const docRef = doc(db, "globals", "falseDuplicates");
    const docSnap = await getDoc(docRef);
    const groupsObj = docSnap.exists() ? docSnap.data().groups || {} : {};
    // Convert object values back to arrays
    const groups = Object.values(groupsObj);
    commit("SET_IGNORED_DUPLICATE_GROUPS", groups);
    return groups;
  } catch (error) {
    console.error("Error fetching ignored duplicate groups:", error);
    return [];
  }
};

export const IGNORE_DUPLICATE_GROUPS = async (
  { commit, state },
  contactIdGroups
) => {
  if (!contactIdGroups || contactIdGroups.length === 0) {
    throw new Error("At least one group is required");
  }

  // Filter out groups with fewer than 2 contacts
  const validGroups = contactIdGroups.filter((group) => group.length >= 2);

  if (validGroups.length === 0) {
    throw new Error("At least one group with 2+ contacts is required");
  }

  // Merge new groups with existing ones, combining overlapping groups
  const mergedGroups = [...state.ignoredDuplicatePairs];

  validGroups.forEach((newGroup) => {
    const newGroupSet = new Set(newGroup);
    let merged = false;

    // Check if this group overlaps with any existing group
    for (let i = 0; i < mergedGroups.length; i++) {
      const existingGroup = mergedGroups[i];
      const hasOverlap = existingGroup.some((id) => newGroupSet.has(id));

      if (hasOverlap) {
        // Merge the groups
        mergedGroups[i] = [...new Set([...existingGroup, ...newGroup])].sort();
        merged = true;
        break;
      }
    }

    // If no overlap, add as new group
    if (!merged) {
      mergedGroups.push([...newGroup].sort());
    }
  });

  // Deduplicate groups (in case merging created duplicates)
  const uniqueGroups = [];
  const seenGroupKeys = new Set();

  mergedGroups.forEach((group) => {
    const sortedGroup = [...group].sort();
    const key = sortedGroup.join("_");
    if (!seenGroupKeys.has(key)) {
      seenGroupKeys.add(key);
      uniqueGroups.push(sortedGroup);
    }
  });

  // Build object with sorted contact IDs as keys
  const groupsObj = {};
  uniqueGroups.forEach((group) => {
    const key = group.join("_");
    groupsObj[key] = group;
  });

  await setDoc(doc(db, "globals", "falseDuplicates"), { groups: groupsObj });

  // Convert back to array for state
  const updatedGroups = Object.values(groupsObj);
  commit("SET_IGNORED_DUPLICATE_GROUPS", updatedGroups);

  return validGroups.length;
};
